import { useCallback, useEffect, useRef, useState } from "react";
function alsServerFehler(fehler) {
    return typeof fehler === "object" && fehler !== null && "status" in fehler
        && typeof fehler.status === "number"
        ? fehler : null;
}
function meldungAus(fehler) {
    return fehler instanceof Error && fehler.message !== ""
        ? fehler.message : "Das Speichern ist fehlgeschlagen.";
}
/**
 * Bündelt die Formularzustände für `me-formular` (ME-Etappe 3, Aufgabe 2):
 *
 *  - Während `aktion()` → `laeuft`: Das Formular sperrt seine Felder (readOnly +
 *    aria-disabled, der Fokus bleibt), der Fuß sperrt beide Ausgänge über `laeuft`.
 *  - 422 mit `fields` → `feldFehler` (je Feld als Prop `fehler`), danach springt der Fokus
 *    über `me-formular.zumErstenFehler()` auf das erste beanstandete Feld. Befunde zu
 *    Feldern außerhalb von `bekannteFelder` — und alle, wenn der Sprung kein Feld findet —
 *    stehen als „<Beschriftung>: <message>“ bzw. „<message>“ in `meldung`: kein Befund verschwindet still.
 *  - 409 → `fremder-stand`: Hinweiszeile mit „Neu laden", Felder gesperrt. Die EINGABEN
 *    BLEIBEN STEHEN — der Haken fasst sie nicht an; sie sind lesbar, bis der Benutzer
 *    selbst „Neu laden" wählt (Review-Fokus 2: nichts geht still verloren). Erst
 *    `neuLaden()` lädt den Serverstand und gibt das Formular frei.
 *  - Alles andere → `meldung`.
 */
export function useFormularZustand(optionen) {
    const [zustand, setZustand] = useState("bereit");
    const [feldFehler, setFeldFehler] = useState({});
    const [meldung, setMeldung] = useState(null);
    const [fremderStand, setFremderStand] = useState(null);
    const formularRef = useRef(null);
    const zustandRef = useRef("bereit");
    const optionenRef = useRef(optionen);
    optionenRef.current = optionen;
    // Sprung zum ersten Fehler erst NACH dem Zeichnen: die Felder tragen `fehler` erst dann.
    const [springen, setSpringen] = useState(0);
    // Befunde der letzten 422, die (noch) an keinem Feld hängen: unbekannte Felder sofort,
    // die bekannten nur, wenn der Sprung kein Feld findet (Feld nicht gezeichnet).
    const restRef = useRef({ unbekannt: [], bekannt: [] });
    const setze = useCallback((z) => {
        zustandRef.current = z;
        setZustand(z);
    }, []);
    const zumErstenFehler = useCallback((form) => {
        form?.zumErstenFehler();
    }, []);
    useEffect(() => {
        if (springen === 0)
            return;
        const gefunden = formularRef.current?.zumErstenFehler() ?? null;
        const { unbekannt, bekannt } = restRef.current;
        const rest = gefunden === null ? [...unbekannt, ...bekannt] : unbekannt;
        // Gleiche Texte nur einmal (zwei Felder mit derselben Meldung ohne Beschriftung).
        if (rest.length > 0)
            setMeldung([...new Set(rest)].join("\n"));
    }, [springen]);
    const aktion = useCallback(async () => {
        if (zustandRef.current !== "bereit")
            return undefined;
        setze("laeuft");
        setFeldFehler({});
        setMeldung(null);
        try {
            const ergebnis = await optionenRef.current.speichern();
            setze("bereit");
            return ergebnis;
        }
        catch (fehler) {
            const server = alsServerFehler(fehler);
            if (server?.status === 409) {
                const k = server.conflict;
                setFremderStand(k?.changedBy !== undefined
                    ? { changedBy: k.changedBy, changedAt: k.changedAt ?? "" } : null);
                setze("fremder-stand");
                return undefined;
            }
            setze("bereit");
            if (server?.status === 422 && server.fields !== undefined && server.fields.length > 0) {
                const bekannt = optionenRef.current.bekannteFelder;
                const istBekannt = bekannt === undefined ? () => true
                    : typeof bekannt === "function" ? bekannt : (f) => bekannt.includes(f);
                const map = {};
                const rest = { unbekannt: [], bekannt: [] };
                for (const f of server.fields) {
                    const beschriftung = optionenRef.current.beschriftungen?.[f.field];
                    const text = beschriftung !== undefined ? `${beschriftung}: ${f.message}` : f.message;
                    if (!istBekannt(f.field)) {
                        rest.unbekannt.push(text);
                        continue;
                    }
                    if (map[f.field] === undefined) {
                        map[f.field] = f.message;
                        rest.bekannt.push(text);
                    }
                }
                restRef.current = rest;
                setFeldFehler(map);
                setSpringen((n) => n + 1);
                return undefined;
            }
            setMeldung(meldungAus(fehler));
            return undefined;
        }
    }, [setze]);
    const zuruecksetzen = useCallback(() => {
        if (zustandRef.current === "laeuft")
            return;
        restRef.current = { unbekannt: [], bekannt: [] };
        setFeldFehler({});
        setMeldung(null);
        setFremderStand(null);
        setze("bereit");
    }, [setze]);
    /*
     * Räumen beim Tippen — von selbst, für JEDE Maske (ME-Etappe 4, P8; Gesamtreview Etappe 3,
     * H6). Bis dahin räumten nur die drei Verwaltungsmasken aus A6, die `feldFehlerRaeumen(name)`
     * im onChange riefen; in den Masken aus A3–A5 (Kunden, Vorgänge, Rechnungen) blieb ein
     * schon korrigiertes Feld bis zum nächsten Speichern rot (`aria-invalid` am inneren Feld).
     *
     * Woher der Haken weiss, WELCHER Befund zum geänderten Feld gehört: Das Element meldet jede
     * Eingabe als `me-geaendert` (bubbles, composed) und trägt seinen Befund als Attribut
     * `fehler` — und das ist der Text, den dieser Haken in `feldFehler` hält. Gesucht wird der
     * Feldname, dessen Text am geänderten Feld steht. So braucht es keine Zuordnung Feld →
     * Servername je Maske, auch dort nicht, wo die Maske Befunde umleitet (AdressListe:
     * Servername → Feld). Tragen mehrere Befunde denselben Text, entscheidet die Kennung des
     * Feldes (`id`/`name` enthält den Feldnamen, „kk-netDays“ → `netDays`); bleibt es
     * mehrdeutig, wird nichts geräumt (der Befund steht dann wie früher bis zum Speichern).
     *
     * Gehorcht wird am Dokument (Einfangphase), nicht am Formular: Der Haken lebt oft länger
     * als das `me-formular` (die Maske öffnet und schliesst), und zählt nur, was IN seinem
     * Formular geschieht.
     */
    const feldFehlerRef = useRef(feldFehler);
    feldFehlerRef.current = feldFehler;
    useEffect(() => {
        const beiAenderung = (ereignis) => {
            const fehler = feldFehlerRef.current;
            if (Object.keys(fehler).length === 0)
                return;
            const form = formularRef.current;
            const feld = ereignis.target;
            if (form === null || !(feld instanceof Element) || !form.contains(feld))
                return;
            const text = feld.getAttribute("fehler");
            if (text === null || text === "")
                return;
            let kandidaten = Object.keys(fehler).filter((name) => fehler[name] === text);
            if (kandidaten.length > 1) {
                const kennung = `${feld.id} ${feld.getAttribute("name") ?? ""}`.toLowerCase();
                kandidaten = kandidaten.filter((name) => kennung.includes(name.toLowerCase()));
            }
            if (kandidaten.length !== 1)
                return;
            const name = kandidaten[0];
            setFeldFehler((alt) => {
                if (alt[name] === undefined)
                    return alt;
                const neu = { ...alt };
                delete neu[name];
                return neu;
            });
        };
        document.addEventListener("me-geaendert", beiAenderung, true);
        return () => document.removeEventListener("me-geaendert", beiAenderung, true);
    }, []);
    const feldFehlerRaeumen = useCallback((name) => {
        setFeldFehler((alt) => {
            if (name === undefined)
                return Object.keys(alt).length === 0 ? alt : {};
            if (alt[name] === undefined)
                return alt;
            const neu = { ...alt };
            delete neu[name];
            return neu;
        });
    }, []);
    const neuLaden = useCallback(async () => {
        // Fixrunde 1, W2: nicht während eines Aufrufs/Ladens — ein Doppelklick lädt nur einmal.
        const vorher = zustandRef.current;
        if (vorher === "laeuft")
            return;
        setze("laeuft");
        try {
            await optionenRef.current.neuLaden();
            setFeldFehler({});
            setMeldung(null);
            setFremderStand(null);
            setze("bereit");
        }
        catch (fehler) {
            // Zurück in den Zustand davor (beim fremden Stand: weiter gesperrt) — der Serverstand
            // ist nicht da, die Eingaben stehen weiter.
            setMeldung(meldungAus(fehler));
            setze(vorher);
        }
    }, [setze]);
    return {
        zustand,
        feldFehler,
        meldung,
        fremderStand,
        laeuft: zustand === "laeuft",
        aktion,
        neuLaden,
        zumErstenFehler,
        zuruecksetzen,
        feldFehlerRaeumen,
        formularProps: {
            ref: formularRef,
            laeuft: zustand === "laeuft",
            fremderStand: zustand === "fremder-stand",
            onNeuLaden: () => { void neuLaden(); },
        },
    };
}
//# sourceMappingURL=useFormularZustand.js.map